import { useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import lottie from "lottie-web";
import PlacesAutocomplete from "react-places-autocomplete";
import { suggestionBackgroundColor } from "../../BookingStyle";

const placeSearchOptions = {
  componentRestrictions: { country: "PE" },
};

const RoutesIconAnimation = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const animation = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: `${process.env.PUBLIC_URL}/assets/raw/routes.json`,
    });

    return () => animation.destroy();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: 45,
        height: 45,
        pointerEvents: "none",
      }}
    />
  );
};

const DestinationAutocompleteField = ({
  value,
  placeholder,
  readOnly,
  onChange,
  onSelect,
  onInputChange,
  onInputClick,
  onMapClick,
  hideRoutesIcon = false,
  showAddRoutesIcon = false,
  error = false,
}) => {
  const hasDestination = Boolean(String(value || "").trim());
  const showIconButton = !hideRoutesIcon;
  const handleIconClick = (event) => {
    event.stopPropagation();
    if (showAddRoutesIcon) {
      onInputClick?.();
      return;
    }
    if (hasDestination) {
      onInputClick?.();
      return;
    }
    onMapClick?.();
  };

  return (
    <PlacesAutocomplete
      value={value}
      onChange={onChange}
      onSelect={onSelect}
      searchOptions={placeSearchOptions}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div key={suggestions.description}>
          <FormControl fullWidth variant="outlined" error={error}>
            <OutlinedInput
              id="outlined-adornment-weight"
              error={error}
              onChange={onInputChange}
              endAdornment={
                showIconButton ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleIconClick}
                      edge="end"
                      sx={{
                        marginRight: hasDestination ? -1.5 : 0,
                        padding: hasDestination ? 0 : undefined,
                        color: hasDestination ? "success.main" : "primary.main",
                        transition:
                          "background-color 0.2s ease, color 0.2s ease",
                        "&:hover": {
                          bgcolor: "primary.main",
                          color: "common.white",
                        },
                      }}
                    >
                      {showAddRoutesIcon ? (
                        <AddOutlinedIcon fontSize="medium" />
                      ) : hasDestination ? (
                        <RoutesIconAnimation />
                      ) : (
                        <LocationOnIcon fontSize="medium" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
              aria-describedby="outlined-weight-helper-text"
              {...getInputProps({
                placeholder,
                className: "location-search-input",
                readOnly,
                onClick: onInputClick,
              })}
            />
            {error && <FormHelperText>Ingrese el destino</FormHelperText>}
          </FormControl>
          <Box
            key={suggestions.description}
            className="autocomplete-dropdown-container suggestionsDestination"
          >
            {loading && <div>Loading...</div>}
            {suggestions.map((suggestion) => {
              const className = suggestion.active
                ? "suggestion-item--active"
                : "suggestion-item";
              const style = suggestion.active
                ? suggestionBackgroundColor("#fafafa")
                : suggestionBackgroundColor("#ffffff");
              return (
                <div
                  key={suggestion.description}
                  {...getSuggestionItemProps(suggestion, {
                    className,
                    style,
                  })}
                >
                  <span>{suggestion.description}</span>
                </div>
              );
            })}
          </Box>
        </div>
      )}
    </PlacesAutocomplete>
  );
};

export default DestinationAutocompleteField;
